import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientService } from '@services';
import { computePortalStats, EMPTY_PORTAL_STATS } from '@utils/portalStats';
import { useTasks } from './useTasks';
import { useSprints } from './useSprints';
import { useEpics } from './useEpics';
import { useTaskStatuses } from './useTaskStatuses';

// Data for the client portal page, with the same shape in both modes:
// - public portal (client with link + PIN): one call to the portal endpoint,
//   which only returns tasks in client-visible statuses plus progress counts.
// - internal preview (team member): regular endpoints, filtered here so the
//   preview shows exactly what the client would see.
export const useClientPortalData = ({ projectId, isPublicPortal, portalToken }) => {
  const internal = !!projectId && !isPublicPortal;

  const publicQuery = useQuery({
    queryKey: ['clientPortalProject', portalToken, projectId],
    queryFn: () => clientService.getPortalProject(portalToken, projectId),
    enabled: !!projectId && isPublicPortal && !!portalToken,
    retry: false,
  });

  const tasksQuery = useTasks({ projectId }, { enabled: internal });
  const sprintsQuery = useSprints({ projectId }, { enabled: internal });
  const epicsQuery = useEpics({ projectId }, { enabled: internal });
  const statusesQuery = useTaskStatuses(projectId, { enabled: internal });

  const internalData = useMemo(() => {
    if (isPublicPortal) return null;
    const tasks = tasksQuery.data?.data || [];
    const clientStatuses = (statusesQuery.data?.data || []).filter((s) => s.clientVisible);
    const visibleNames = new Set(clientStatuses.map((s) => s.name));
    return {
      visibleTasks: tasks.filter((t) => visibleNames.has(t.status)),
      stats: computePortalStats(tasks),
      clientStatuses,
      sprints: sprintsQuery.data?.data || [],
      epics: epicsQuery.data?.data || [],
    };
  }, [isPublicPortal, tasksQuery.data, statusesQuery.data, sprintsQuery.data, epicsQuery.data]);

  const publicData = useMemo(() => {
    if (!isPublicPortal) return null;
    const data = publicQuery.data?.data;
    return {
      visibleTasks: data?.visibleTasks || [],
      stats: data?.stats || EMPTY_PORTAL_STATS,
      clientStatuses: data?.clientStatuses || [],
      sprints: data?.sprints || [],
      epics: data?.epics || [],
    };
  }, [isPublicPortal, publicQuery.data]);

  const isLoading = isPublicPortal
    ? publicQuery.isLoading
    : tasksQuery.isLoading || sprintsQuery.isLoading || epicsQuery.isLoading || statusesQuery.isLoading;

  return { ...(isPublicPortal ? publicData : internalData), isLoading };
};
